import { useEffect, useState } from 'react';

import { pipe } from 'fp-ts/lib/function';
import { add, toNumber, uniqBy } from 'lodash/fp';

import { User } from '@domain/auth';
import { SITECORE_USER_TYPES } from '@domain/clubs';
import { RI_FISCAL_AGENT, SPECIAL_REPRESENTATIVE } from '@domain/districts';

import { getFormattedDistricts } from '@use-cases/districts';
import { getUserType } from '@use-cases/sitecore/helpers/getUserTypes';

import { getBackendLogoutUrl, getWebappLogoutUrl } from '@repositories/auth';
import { useUserAccount } from '@repositories/auth/hooks';
import { useFetchDistrictsForLeadership } from '@repositories/districts/hooks/useFetchDistrictsForLeadership';
import { useSearchDESByIndividualId } from '@repositories/districts/hooks/useSearchDESByIndividualId';

import { constructFullName } from '@utils/constructFullName';
import { getRotaryYear } from '@utils/datetime';
import { getDistrictsList } from '@utils/districts';
import {
  FEATURE_SITECORE_HEADER,
  FEATURE_SITECORE_HOMEPAGE,
  FEATURE_SITECORE_LANDING_PAGE,
  isEnabled,
} from '@utils/features';
import { getClubLeadership, getUserClubs } from '@utils/getUserClubs';

import { SITECORE_INDIVIDUAL_TOKEN } from '@sitecore/constants';
import { getAllDayLabel } from '@sitecore/sitecoreTranslations';

import { useTranslation } from '@external/react-i18next';

import {
  ClubAffiliation,
  ClubLeadership,
  DistrictType,
  FetchUserAccountQuery,
} from '@typings/operations';

export const useSiteCoreUserType = (
  user: User | undefined,
  isProcessingLogin: boolean
) => {
  const { t } = useTranslation();
  const individualId = user?.individualId || '';
  const { data, loading } = useUserAccount(individualId);
  const [areClubsAvailable, setClubsAvailable] = useState(false);
  const [
    fetchSearchDES,
    { data: searchDESData, loading: searchDESLoading },
  ] = useSearchDESByIndividualId();

  const [
    fetchDistrictsForLeadership,
    { data: districtsForLeadership, loading: districtsForLeadershipLoading },
  ] = useFetchDistrictsForLeadership();

  useEffect(() => {
    if (
      individualId &&
      (isEnabled(FEATURE_SITECORE_HEADER) ||
        isEnabled(FEATURE_SITECORE_HOMEPAGE) ||
        isEnabled(FEATURE_SITECORE_LANDING_PAGE))
    ) {
      fetchSearchDES({
        variables: { individualId },
      });
      fetchDistrictsForLeadership({
        variables: {
          roles: [RI_FISCAL_AGENT, SPECIAL_REPRESENTATIVE],
          rotaryYear: pipe(getRotaryYear(), toNumber, add(1)),
        },
      });
      setClubsAvailable(true);
    }
  }, [individualId]);

  if (
    isEnabled(FEATURE_SITECORE_HEADER) ||
    isEnabled(FEATURE_SITECORE_HOMEPAGE) ||
    isEnabled(FEATURE_SITECORE_LANDING_PAGE)
  ) {
    if (!isProcessingLogin && !user?.isLoggedIn) {
      window.localStorage.setItem(SITECORE_INDIVIDUAL_TOKEN, '');
      return SITECORE_USER_TYPES.UNAUTHENTICATED;
    }

    if (loading || searchDESLoading || districtsForLeadershipLoading) {
      window.localStorage.setItem(SITECORE_INDIVIDUAL_TOKEN, '');
      return SITECORE_USER_TYPES.USER_LOADING;
    }

    const accountInfo = {
      ...user?.attributes,
      ...data?.getIndividual,
    };
    const DESInfo = searchDESData?.searchDESByIndividualId.affiliationInfo;

    const clubsAffiliation =
      ((accountInfo as FetchUserAccountQuery['getIndividual'])
        ?.clubAffiliations as ClubAffiliation[]) || [];

    const userClubsForClubsMenuItems = getUserClubs(clubsAffiliation);
    const userClubsForDistrictsMenuItems = getUserClubs(clubsAffiliation, true);

    const specRepDistricts =
      districtsForLeadership?.getDistrictsForLeadership.districts || [];

    const districtsInfo: DistrictType[] = DESInfo
      ? getFormattedDistricts(DESInfo.districts)
      : getDistrictsList(userClubsForDistrictsMenuItems);

    const userClubs: ClubLeadership[] = getClubLeadership(
      (userClubsForClubsMenuItems as ClubLeadership[]) || [],
      t
    );
    const desClubs: ClubLeadership[] = getClubLeadership(
      (DESInfo?.clubs as ClubLeadership[]) || [],
      t
    );

    const clubs = uniqBy('clubId', [...userClubs, ...desClubs]);
    const districts = uniqBy('id', [...districtsInfo, ...specRepDistricts]);

    const userTypeTranslation = (userRole: string) => {
      switch (userRole) {
        case SITECORE_USER_TYPES.NON_MEMBER:
          return t('sitecore-non-member-greeting', 'Participant');

        case SITECORE_USER_TYPES.MEMBER:
          return t('sitecore-member-greeting', 'Member');

        case SITECORE_USER_TYPES.LEADER:
          return t('sitecore-leader-greeting', 'Leader');

        default:
          return '';
      }
    };

    const IsDESInfoAvailable = !!DESInfo && clubs && clubs.length > 0;

    const destination = window.location.pathname;
    const newUser =
      !individualId &&
      (destination.endsWith('/account/select-member-status') ||
        destination.endsWith('/account/map-by-email') ||
        destination.endsWith('/contact'));
    const existingUser =
      areClubsAvailable && !isProcessingLogin && user?.isLoggedIn;

    let userType = SITECORE_USER_TYPES.USER_LOADING;
    if (newUser) {
      userType = SITECORE_USER_TYPES.UNAUTHENTICATED;
    } else if (existingUser) {
      userType = getUserType(clubsAffiliation, IsDESInfoAvailable);
    }
    const userRole = userTypeTranslation(userType);

    const { firstName, lastName, middleName } = accountInfo;
    const fullName = constructFullName(accountInfo);
    const backendLogoutUrl = getBackendLogoutUrl();
    const logOutUrl = getWebappLogoutUrl(backendLogoutUrl);
    const weekDayLabels = getAllDayLabel(t);
    const districtLabel = t(
      'search.members.filters-district-label',
      'District'
    );

    const profileInfo = {
      firstName,
      lastName,
      middleName,
      fullName,
      userRole,
    };

    window.localStorage.setItem(
      SITECORE_INDIVIDUAL_TOKEN,
      JSON.stringify({
        individualId,
        clubs,
        districts,
        profileInfo,
        logOutUrl,
        weekDayLabels,
        districtLabel,
      })
    );
    return userType;
  }
  return SITECORE_USER_TYPES.UNAUTHENTICATED;
};
